import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

/**
 * UI Store
 * Manages global UI state including theme, modals, sidebars, and notifications
 */
const useUIStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ========== STATE ==========
        theme: 'light',
        sidebarOpen: true,
        mobileMenuOpen: false,
        activeModal: null,
        modalData: null,
        notifications: [],

        // ========== THEME ACTIONS ==========

        /**
         * Set theme
         * @param {'light'|'dark'} theme - Theme to set
         */
        setTheme: (theme) => {
          set({ theme }, false, 'ui/setTheme');
          document.documentElement.classList.toggle('dark', theme === 'dark');
        },

        /**
         * Toggle between light and dark theme
         */
        toggleTheme: () => {
          const currentTheme = get().theme;
          const newTheme = currentTheme === 'light' ? 'dark' : 'light';
          set({ theme: newTheme }, false, 'ui/toggleTheme');
          document.documentElement.classList.toggle(
            'dark',
            newTheme === 'dark'
          );
        },

        // ========== SIDEBAR ACTIONS ==========

        /**
         * Toggle sidebar open/closed
         */
        toggleSidebar: () =>
          set(
            (state) => ({ sidebarOpen: !state.sidebarOpen }),
            false,
            'ui/toggleSidebar'
          ),

        /**
         * Set sidebar state
         * @param {boolean} open - Whether sidebar should be open
         */
        setSidebarOpen: (open) =>
          set({ sidebarOpen: open }, false, 'ui/setSidebarOpen'),

        // ========== MOBILE MENU ACTIONS ==========

        /**
         * Toggle mobile menu open/closed
         */
        toggleMobileMenu: () =>
          set(
            (state) => ({ mobileMenuOpen: !state.mobileMenuOpen }),
            false,
            'ui/toggleMobileMenu'
          ),

        /**
         * Set mobile menu state
         * @param {boolean} open - Whether mobile menu should be open
         */
        setMobileMenuOpen: (open) =>
          set({ mobileMenuOpen: open }, false, 'ui/setMobileMenuOpen'),

        // ========== MODAL ACTIONS ==========

        /**
         * Open a modal
         * @param {string} modalId - Modal identifier
         * @param {any} data - Optional data to pass to modal
         */
        openModal: (modalId, data) =>
          set({ activeModal: modalId, modalData: data }, false, 'ui/openModal'),

        /**
         * Close active modal
         */
        closeModal: () =>
          set({ activeModal: null, modalData: null }, false, 'ui/closeModal'),

        // ========== NOTIFICATION ACTIONS ==========

        /**
         * Add a notification
         * @param {Object} notification - Notification object
         * @param {'success'|'error'|'info'|'warning'} notification.type - Notification type
         * @param {string} notification.message - Notification message
         * @param {number} notification.duration - Duration in ms (default 5000)
         */
        addNotification: (notification) => {
          const id = crypto.randomUUID();
          const newNotification = {
            id,
            type: notification.type || 'info',
            message: notification.message,
            duration: notification.duration || 5000,
          };

          set(
            (state) => ({
              notifications: [...state.notifications, newNotification],
            }),
            false,
            'ui/addNotification'
          );

          // Auto-remove notification after duration
          if (newNotification.duration > 0) {
            setTimeout(() => {
              get().removeNotification(id);
            }, newNotification.duration);
          }

          return id;
        },

        /**
         * Remove a notification by ID
         * @param {string} id - Notification ID
         */
        removeNotification: (id) =>
          set(
            (state) => ({
              notifications: state.notifications.filter((n) => n.id !== id),
            }),
            false,
            'ui/removeNotification'
          ),

        /**
         * Clear all notifications
         */
        clearNotifications: () =>
          set({ notifications: [] }, false, 'ui/clearNotifications'),

        // ========== UTILITY ACTIONS ==========

        /**
         * Reset UI state to defaults
         */
        reset: () =>
          set(
            {
              sidebarOpen: true,
              mobileMenuOpen: false,
              activeModal: null,
              modalData: null,
              notifications: [],
            },
            false,
            'ui/reset'
          ),
      }),
      {
        name: 'ui-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          theme: state.theme,
          sidebarOpen: state.sidebarOpen,
        }),
        version: 1,
      }
    ),
    {
      name: 'UIStore',
      enabled: import.meta.env.DEV,
    }
  )
);

// ========== SELECTORS ==========

export const selectTheme = (state) => state.theme;
export const selectSidebarOpen = (state) => state.sidebarOpen;
export const selectMobileMenuOpen = (state) => state.mobileMenuOpen;
export const selectActiveModal = (state) => state.activeModal;
export const selectModalData = (state) => state.modalData;
export const selectNotifications = (state) => state.notifications;

// ========== HELPER FUNCTIONS ==========

/**
 * Notification helper functions - can be called from anywhere
 */
export const notify = {
  success: (message, duration) => {
    return useUIStore
      .getState()
      .addNotification({ type: 'success', message, duration });
  },
  error: (message, duration) => {
    return useUIStore
      .getState()
      .addNotification({ type: 'error', message, duration });
  },
  info: (message, duration) => {
    return useUIStore
      .getState()
      .addNotification({ type: 'info', message, duration });
  },
  warning: (message, duration) => {
    return useUIStore
      .getState()
      .addNotification({ type: 'warning', message, duration });
  },
};

export default useUIStore;
